import { useState } from "react";
import { useUpdateRelationshipLevel, useUpdateTop30 } from "@/hooks/queries";
import { RelationshipLevelTable } from "@components/RelationshipLevelTable";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import styles from "./styles.module.css";

type Props = {
  profileId: string;
  currentTop30: boolean | null | undefined;
  currentLevel: number | null | undefined;
  onChanged?: () => void;
};

export const Top30Checkbox = ({
  profileId,
  currentTop30,
  currentLevel,
  onChanged,
}: Props) => {
  const updateTop30 = useUpdateTop30();
  const updateLevel = useUpdateRelationshipLevel();
  const [pickerOpen, setPickerOpen] = useState(false);

  const isPending = updateTop30.isPending || updateLevel.isPending;

  const handleChange = (checked: boolean) => {
    if (!checked) {
      updateTop30.mutate(
        { profileId, top30: false },
        { onSuccess: () => onChanged?.() }
      );
      return;
    }
    // Checking Top 30: a contact must have a relationship level first.
    if (currentLevel != null) {
      updateTop30.mutate(
        { profileId, top30: true },
        { onSuccess: () => onChanged?.() }
      );
    } else {
      setPickerOpen(true);
    }
  };

  const handlePickLevel = async (level: number | null) => {
    setPickerOpen(false);
    if (level === null) return; // can't be Top 30 without a level
    // Invariant-safe order: set the level first, then flag Top 30.
    await updateLevel.mutateAsync({ profileId, level });
    await updateTop30.mutateAsync({ profileId, top30: true });
    onChanged?.();
  };

  return (
    <>
      <FormControlLabel
        className={styles.label}
        control={
          <Checkbox
            size="small"
            checked={!!currentTop30}
            disabled={isPending}
            onChange={(e) => handleChange(e.target.checked)}
            inputProps={{ "aria-label": "Top 30" }}
          />
        }
        label="Top 30"
      />
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)}>
        <DialogTitle>Select a Relationship Level</DialogTitle>
        <DialogContent>
          <DialogContentText className={styles.pickerText}>
            You have selected Top 30 on a contact without a Relationship Level.
            Please select it now:
          </DialogContentText>
          <RelationshipLevelTable
            currentLevel={currentLevel}
            onSelect={handlePickLevel}
            includeNone={false}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
